import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './ManagerContractMobile.css';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { FaTools } from 'react-icons/fa';
import API_BASE_URL from './config/api';

import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import Header from './Components/Header/BlueHeader';
import Sidebar from './Components/Sidebar/Sidebar';
import PriorityServiceModal from './Components/PriorityServiceModal/PriorityServiceModal';

function MechanicalLayout() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [buildingName, setBuildingName] = useState("");
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState("");
  const [maintenanceCycles, setMaintenanceCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [hasMechanicalContract, setHasMechanicalContract] = useState(null);
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showStatusBanner, setShowStatusBanner] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || "";
  let buildingId = "";

  if (userStr) {
    try {
      const userObj = JSON.parse(userStr);
      buildingId = userObj.buildingId || "";
    } catch (err) {
      console.error("Error parsing user data:", err);
    }
  }

  useEffect(() => {
    const fetchBuildingData = async () => {
      if (!buildingId) {
        setIsLoading(false);
        return;
      }
      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/getBuildingById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ buildingId })
          }
        );
        if (!resp.ok) {
          throw new Error(`Error fetching building data: ${resp.status}`);
        }
        const data = await resp.json();
        setBuildingName(data.name || "Unknown");
        setBuildingManagerName(Object.prototype.hasOwnProperty.call(data, 'manager') ? data.manager || '' : data.managerProfile?.manager || '');
        setBuildingPhotoUrl(data.photo_url || "");

        const contracts = data.contracts || [];
        setHasMechanicalContract(contracts.includes("mechanical-room"));
        setHasProjectContract(contracts.includes('project'));
        setHasValveAuditContract(contracts.includes('valve-audit'));
      } catch (err) {
        console.error("fetchBuildingData error:", err);
        setHasMechanicalContract(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBuildingData();
  }, [token, buildingId]);

  const handleNavigate = (key) => {
    switch (key) {
      case 'hvac':
        navigate('/');
        break;
      case 'mech':
        navigate('/mechanical-room');
        break;
      case 'dryerVents':
        navigate('/dryer-vents');
        break;
      case 'stackCleaning':
        navigate('/stack-cleaning');
        break;
      case 'waterSavings':
        navigate('/water-savings');
        break;
      case 'reports':
        navigate('/reports');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'valveAudit':
        navigate('/valve-audit');
        break;
      default:
        break;
    }
  };

  const handleServiceRequest = (message) => {
    setStatusMessage(message);
    setShowStatusBanner(true);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setShowStatusBanner(false), 5000);
  };

  const EmptyState = () => (
    <div style={{ width: '90%', margin: '2rem auto 0', backgroundColor: '#f8f9fa' }}>
      <Card className="shadow-lg border-0 rounded-lg">
        <Card.Body className="text-center p-5">
          <div style={{ fontSize: '3rem', color: '#224982', marginBottom: '1.5rem' }}>
            <FaTools />
          </div>
          <Card.Title style={{ 
            fontSize: '2rem', 
            marginBottom: '1.5rem', 
            color: '#224982',
            fontWeight: '600'
          }}>
            Mechanical Room Maintenance
          </Card.Title>
          <Card.Text style={{ 
            color: '#596272', 
            marginBottom: '2rem',
            fontSize: '1.1rem',
            lineHeight: '1.6',
            maxWidth: '800px',
            margin: '0 auto 2rem'
          }}>
            Streamline your mechanical room operations with Hudson's comprehensive management solution. 
            Get real-time monitoring, preventive maintenance scheduling, and detailed equipment tracking all in one place.
          </Card.Text>
          <div style={{ fontSize: '1.15rem', color: '#224982', fontWeight: 500 }}>
            Email <a href="mailto:service@hudsonhvac.ca">service@hudsonhvac.ca</a> for a free proposal
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const LoadingState = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <Spinner animation="border" role="status" style={{ color: '#224982' }}>
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  const outletContext = {
    buildingName,
    buildingPhotoUrl,
    maintenanceCycles,
    selectedCycle,
    setSelectedCycle,
    selectedMaintenance,
    hasMechanicalContract,
    isLoading,
    buildingId,
  };

  return (
    <>
      {showStatusBanner && (
        <Alert
          variant="success"
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            margin: 0,
            padding: '0.5rem 1rem',
            width: 'auto',
            maxWidth: '90vw'
          }}
        >
          <strong>{statusMessage}</strong>
        </Alert>
      )}
      <div style={{ display: 'flex' }}>
        <div
          className="dashboard-sidebar-host"
          onMouseEnter={() => setCollapsed(false)}
          onMouseLeave={() => setCollapsed(true)}
          style={{ height: '100vh' }}
        >
          <Sidebar
            managerName={buildingManagerName}
            activeKey="mech"
            onNavigate={handleNavigate}
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
            buildingName={buildingName}
            buildingPhotoUrl={buildingPhotoUrl}
            showProjectsTab={hasProjectContract}
            showValveAuditTab={hasValveAuditContract}
          />
        </div>
        <div
          className="dashboard-content manager-contract-page"
          style={{
            flex: 1,
            minWidth: 0,
            marginLeft: collapsed ? 64 : 220,
            transition: 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            transform: 'translate3d(0,0,0)',
            willChange: 'margin-left',
            backfaceVisibility: 'hidden',
          }}
        >
          <DashboardNavbar
            logoutInSidebar
            buildingName={buildingName}
            buildingId={buildingId}
            contractLabel="Mechanical Room"
          />

          <Header
            maintenanceCycles={maintenanceCycles}
            selectedCycle={selectedCycle}
            setSelectedCycle={setSelectedCycle}
            selectedMaintenance={selectedMaintenance}
            contractLabel="Mechanical Room"
            buildingName={buildingName}
            hideMaintenanceCycle
            onRequestEmergency={() => setShowPriorityModal(true)}
          />

          {isLoading ? (
            <LoadingState />
          ) : !hasMechanicalContract ? (
            <EmptyState />
          ) : (
            <div className="mechanical-page-content" style={{ padding: '2rem' }}>
              <Outlet context={outletContext} />
            </div>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'center', color: '#888', fontSize: '0.95rem', margin: '1.5rem 0 0.5rem 0' }}>
        © 2025 Hudson Condominium Solutions Inc.
      </div>

      <PriorityServiceModal
        show={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        buildingName={buildingName}
        onServiceRequest={handleServiceRequest}
      />
    </>
  );
}

export default MechanicalLayout;


